import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

// Liste de comptes administrée (suivi automatique des nouveaux membres, réponse automatique « Aide & support »…).
export const AccountListManager = ({ isSuper, endpoint, title, icon: Icon, description, successMsg, testPrefix, children, onLoaded, extraPayload }) => {
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(endpoint).then(({ data }) => { setSelected(data.users || []); onLoaded?.(data); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const search = async () => {
    if (q.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}&limit=8`); setResults(data.users || []); } catch { setResults([]); }
  };
  const add = (u) => { if (!selected.find((x) => x.id === u.id)) setSelected([...selected, u]); setResults([]); setQ(""); };
  const remove = (id) => setSelected(selected.filter((x) => x.id !== id));
  const save = async () => {
    setSaving(true);
    try { await api.patch(endpoint, { ids: selected.map((u) => u.id), ...(extraPayload?.() || {}) }); toast.success(successMsg); }
    catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="yalola-card p-5" data-testid={`${testPrefix}-manager`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={17} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold font-display text-lg">{title}</div>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>{description}</p>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testPrefix}-selected`}>
        {selected.length === 0 && <span className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Aucun compte sélectionné.</span>}
        {selected.map((u) => (
          <span key={u.id} className="chip flex items-center gap-1.5" data-testid={`${testPrefix}-chip-${u.id}`}>
            {u.name}
            {isSuper && <button onClick={() => remove(u.id)} className="opacity-60 hover:opacity-100" data-testid={`${testPrefix}-remove-${u.id}`}><X size={12} /></button>}
          </span>
        ))}
      </div>
      {isSuper && (
        <>
          <div className="flex gap-2 items-center relative">
            <Input placeholder="Rechercher un compte à ajouter (nom ou email)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid={`${testPrefix}-search`} />
            <Button variant="outline" className="rounded-full" onClick={search} data-testid={`${testPrefix}-search-btn`}>Chercher</Button>
          </div>
          {results.length > 0 && (
            <div className="mt-2 border rounded-xl divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid={`${testPrefix}-results`}>
              {results.map((u) => (
                <button key={u.id} onClick={() => add(u)} className="w-full text-left px-3 py-2 text-sm hover:bg-[rgba(0,0,0,0.04)] flex items-center justify-between" data-testid={`${testPrefix}-add-${u.id}`}>
                  <span>{u.name} <span className="opacity-50">· {u.email}</span></span>
                  <Plus size={14} />
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {children}
      {isSuper && (
        <div className="flex justify-end mt-3">
          <Button className="pill-primary rounded-full" onClick={save} disabled={saving} data-testid={`${testPrefix}-save`}>{saving ? "…" : "Enregistrer"}</Button>
        </div>
      )}
    </div>
  );
};
